import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { NumberInput } from '../ui/NumberInput';
import { marginLabel, sourceLabel } from './format';
import { Tiles } from './Tiles';
import { COST_KINDS, FINANCE_KEY, PROFIT_RESOURCE, type CostResult, type EventCost, type EventCostKind, type Profit } from './types';
import './finance.css';
import { sameValues, useRowGuard, useUnsavedGuard } from '../ui/unsaved';

const STATUS: Record<string, string> = { proponowany: 'proponowany', potwierdzony: 'potwierdzony', odmowa: 'odmowa' };
export const profitKey = (eventId: string) => [...FINANCE_KEY, 'profit', eventId] as const;

/**
 * "Plan z kosztorysu" next to "Obsada (rzeczywiste)" and (F45) "Rozliczone" — approved people's settlements, shown
 * only when there are some (and the reader may see them); the column the result uses is in bold.
 */
function PlanVsActual({ p }: { p: Profit }) {
  const a = p.actual;
  const st = p.settled;
  const used = (col: 'kosztorys' | 'obsada' | 'rozliczenia') => p.basis === col;
  const cell = (v: number | null, on: boolean) => <td className={`num${on ? ' used' : ''}`}>{v === null ? '—' : formatPln(v)}</td>;
  const row = (label: string, plan: number, actual: number | null, settled: number | null) => (
    <tr>
      <th scope="row">{label}</th>
      {cell(plan, used('kosztorys'))}
      {cell(actual, used('obsada'))}
      {st && cell(settled, used('rozliczenia'))}
    </tr>
  );
  const sum = (key: keyof CostResult, label: string) => {
    const value = (r: CostResult | null, on: boolean) => (key === 'marginBp' ? <td className={`num${on ? ' used' : ''}`}>{r ? marginLabel(r.marginBp) : '—'}</td> : cell(r ? (r[key] as number) : null, on));
    return (
      <tr>
        <th scope="row">{label}</th>
        {value(p.plan, used('kosztorys'))}
        {value(a, used('obsada'))}
        {st && value(st, used('rozliczenia'))}
      </tr>
    );
  };
  const same = (v: number, col: CostResult | null) => (col ? v : null);
  return (
    <section className="fin-section" aria-label="Plan i obsada">
      <h3>Koszty: plan z kosztorysu, obsada{st ? ' i rozliczenia' : ''}</h3>
      <div className="table-wrap">
        <table className="table fin-table">
          <thead>
            <tr>
              <th scope="col" />
              <th scope="col" className="num">Plan (kosztorys)</th>
              <th scope="col" className="num">Rzeczywiste (obsada, dopożyczenia)</th>
              {st && <th scope="col" className="num">Rozliczone</th>}
            </tr>
          </thead>
          <tbody>
            {row('Ludzie (usługi z funkcją w obsadzie)', p.people.planGrosze, p.people.actualGrosze, p.people.settledGrosze)}
            {p.servicesGrosze > 0 && row('Inne usługi (koszt z kosztorysu)', p.servicesGrosze, same(p.servicesGrosze, a), same(p.servicesGrosze, st))}
            {row('Sprzęt (koszt w kosztorysie / dopożyczenia)', p.equipmentGrosze, p.equipmentActualGrosze, same(p.equipmentActualGrosze ?? p.equipmentGrosze, st))}
            {row('Transport własny', p.transportGrosze, same(p.transportGrosze, a), same(p.transportGrosze, st))}
            {row('Koszty ręczne', p.manualGrosze, same(p.manualGrosze, a), same(p.manualGrosze, st))}
          </tbody>
          <tfoot>
            {sum('costGrosze', 'Koszty razem')}
            {sum('resultGrosze', 'Wynik')}
            {sum('marginBp', 'Marża')}
          </tfoot>
        </table>
      </div>
      <p className="hint">
        {used('rozliczenia')
          ? 'Wynik liczony z zatwierdzonych rozliczeń ludzi (z nadgodzinami i wydatkami); osoby z obsady bez zatwierdzonego rozliczenia – stawka × dni.'
          : used('kosztorys')
            ? 'Obsady ani dopożyczeń jeszcze nie ma: wynik liczony z planu w kosztorysie.'
            : 'Wynik liczony z obsady (stawki osób w funkcjach × dni); funkcje, których obsada jeszcze nie pokrywa, z planu. Osoby proponowane liczą się jako koszt.'}
      </p>
      {p.subrentals && p.subrentals.count > 0 && (
        <p className="hint">
          Dopożyczenia tego wydarzenia: {p.subrentals.count} · {formatPln(p.subrentals.totalGrosze)} — w kolumnie „Rzeczywiste” zastępują ceny dostawców z kosztorysu.
        </p>
      )}
      {p.transportUnknown && <p className="fin-warn">Transport bez kosztu: brak km na karcie wydarzenia albo stawki kosztowej w „Finanse → Ustawienia”.</p>}
    </section>
  );
}

function CrewCosts({ p }: { p: Profit }) {
  if (p.crew.length === 0) return null;
  const rates = p.crew.some((c) => 'rateGrosze' in c);
  return (
    <section className="fin-section" aria-label="Obsada">
      <h3>Obsada</h3>
      <div className="table-wrap">
        <table className="table fin-table">
          <thead>
            <tr>
              <th scope="col">Osoba</th>
              <th scope="col">Funkcja</th>
              <th scope="col">Status</th>
              <th scope="col" className="num">Dni</th>
              {rates && <th scope="col" className="num">Stawka</th>}
              {rates && <th scope="col" className="num">Koszt</th>}
            </tr>
          </thead>
          <tbody>
            {p.crew.map((c) => (
              <tr key={c.assignmentId} className={c.status === 'odmowa' ? 'muted' : undefined}>
                <td>{c.personName}</td>
                <td>{c.functionName}</td>
                <td className={c.status === 'proponowany' ? 'fin-forecast' : undefined}>{STATUS[c.status]}</td>
                <td className="num">{c.days}</td>
                {rates && <td className="num">{c.rateGrosze == null ? <span className="fin-warn">brak stawki</span> : formatPln(c.rateGrosze)}</td>}
                {rates && <td className="num">{c.costGrosze == null ? '—' : formatPln(c.costGrosze)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rates && <p className="hint">Stawki osób widzi tylko ktoś z prawem do stawek w „Osoby”; tu są same sumy.</p>}
      {p.people.missingRates > 0 && <p className="fin-warn">Bez stawki: {p.people.missingRates} — uzupełnij w „Osoby” (stawka w funkcji albo domyślna funkcji).</p>}
    </section>
  );
}

/** One manual cost: amount and description edited in place (Enter or leaving saves), × removes at once. */
function CostRow({ cost, editable, onChanged }: { cost: EventCost; editable: boolean; onChanged: () => void }) {
  const [amount, setAmount] = useState(plnToInput(cost.amountGrosze));
  const [description, setDescription] = useState(cost.description);
  const [error, setError] = useState('');
  useEffect(() => {
    setAmount(plnToInput(cost.amountGrosze));
    setDescription(cost.description);
  }, [cost.version, cost.amountGrosze, cost.description]);
  /** Saves what changed; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    let amountGrosze: number;
    try {
      amountGrosze = parsePln(amount);
    } catch (e) {
      setError((e as Error).message);
      return false;
    }
    const body = { ...(amountGrosze !== cost.amountGrosze ? { amountGrosze } : {}), ...(description.trim() !== cost.description ? { description: description.trim() } : {}) };
    if (Object.keys(body).length === 0) return true;
    let ok = true;
    try {
      await api(`/api/finance/costs/${cost.id}`, { method: 'PATCH', body: { ...body, version: cost.version } });
      setError('');
    } catch (e) {
      ok = false;
      setError(e instanceof ApiError && e.status === 409 ? 'Ktoś w tym czasie zmienił ten koszt. Pokazuję aktualne wartości.' : e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się zapisać');
    }
    onChanged();
    return ok;
  }
  // owner 2026-10-23 21:14: typed and not saved (leaving the cell saves): leaving the card or tab waits or asks
  const row = useRowGuard(editable && !sameValues({ amount, description: description.trim() }, { amount: plnToInput(cost.amountGrosze), description: cost.description }), persist);
  const save = row.save;
  async function remove() {
    try {
      await api(`/api/finance/costs/${cost.id}?version=${cost.version}`, { method: 'DELETE' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się usunąć');
    }
    onChanged();
  }
  const keys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    }
  };
  return (
    <>
      <tr ref={row.ref}>
        <td>{COST_KINDS[cost.kind]}</td>
        <td>{editable ? <input className="cell-input" aria-label={`Opis: ${COST_KINDS[cost.kind]}`} value={description} onChange={(e) => setDescription(e.target.value)} onBlur={() => void save()} onKeyDown={keys} /> : cost.description}</td>
        <td className="num">
          {editable ? <NumberInput className="cell-input num" kind="money" aria-label={`Kwota: ${COST_KINDS[cost.kind]} ${cost.description}`.trim()} value={amount} onValueChange={setAmount} onBlur={() => void save()} onKeyDown={keys} /> : formatPln(cost.amountGrosze)}
        </td>
        {editable && (
          <td className="q-actions">
            <button className="btn icon ghost" type="button" aria-label={`Usuń koszt: ${COST_KINDS[cost.kind]} ${cost.description}`.trim()} title="Usuń koszt" onClick={() => void remove()}>
              ×
            </button>
          </td>
        )}
      </tr>
      {error && (
        <tr className="sheet-row-error">
          <td colSpan={4} role="alert">
            {error}
          </td>
        </tr>
      )}
    </>
  );
}

function ManualCosts({ eventId, p, editable, onChanged }: { eventId: string; p: Profit; editable: boolean; onChanged: () => void }) {
  const [kind, setKind] = useState<EventCostKind>('hotel');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const add = useMutation({
    mutationFn: (body: object) => api(`/api/finance/events/${eventId}/costs`, { method: 'POST', body }),
    onSuccess: () => {
      setDescription('');
      setAmount('');
      onChanged();
    },
  });
  const [localError, setLocalError] = useState('');
  function bodyOf(): object | null {
    try {
      const amountGrosze = parsePln(amount);
      setLocalError('');
      return { kind, amountGrosze, ...(description.trim() ? { description: description.trim() } : {}) };
    } catch (err) {
      setLocalError((err as Error).message);
      return null;
    }
  }
  // owner 2026-10-23 21:14: a cost typed and not added: leaving asks first
  const addRef = useUnsavedGuard({
    dirty: editable && (description.trim() !== '' || amount.trim() !== ''),
    save: async () => {
      const body = bodyOf();
      if (!body) return false;
      await add.mutateAsync(body);
      return true;
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = bodyOf();
    if (body) add.mutate(body);
  }
  const error = localError || (add.error instanceof ApiError ? (fieldIssues(add.error)[0]?.message ?? add.error.message) : '');
  return (
    <section className="fin-section" aria-label="Koszty ręczne">
      <h3>Koszty ręczne</h3>
      {p.costs.length === 0 && !editable && <p className="muted">Brak kosztów ręcznych.</p>}
      {p.costs.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table">
            <thead>
              <tr>
                <th scope="col">Rodzaj</th>
                <th scope="col">Opis</th>
                <th scope="col" className="num">Kwota</th>
                {editable && (
                  <th scope="col">
                    <span className="visually-hidden">Działania</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {p.costs.map((c) => (
                <CostRow key={c.id} cost={c} editable={editable} onChanged={onChanged} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editable && (
        <form ref={addRef} className="fin-add" aria-label="Dodaj koszt" onSubmit={submit}>
          <label className="field">
            <span>Rodzaj</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as EventCostKind)}>
              {(Object.keys(COST_KINDS) as EventCostKind[]).map((k) => (
                <option key={k} value={k}>
                  {COST_KINDS[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Opis</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="np. nocleg ekipy" autoComplete="off" />
          </label>
          <label className="field">
            <span>Kwota (zł)</span>
            <NumberInput kind="money" value={amount} onValueChange={setAmount} />
          </label>
          <button className="btn" type="submit" disabled={add.isPending}>
            Dodaj koszt
          </button>
        </form>
      )}
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * "Rentowność" on the event card (F29; by default the owner only): revenue from the quote (accepted, else a
 * forecast), the plan from the quote's costs next to the crew's actual costs, own transport, manual costs; result
 * and margin. No "are you sure" anywhere.
 */
export function ProfitTab({ eventId }: { eventId: string }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const profit = useQuery({ queryKey: profitKey(eventId), queryFn: () => api<Profit>(`/api/finance/events/${eventId}/profit`) });
  if (!me) return null;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: FINANCE_KEY });
  const p = profit.data;
  return (
    <div role="tabpanel" aria-label="Rentowność" className="fin-profit" data-feature-id="finance.profitability">
      {profit.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {profit.error && <p className="alert" role="alert">{profit.error instanceof ApiError ? profit.error.message : 'Nie udało się wczytać rentowności'}</p>}
      {p && (
        <>
          <Tiles
            revenue={p.revenue.netGrosze}
            revenueNote={`${sourceLabel(p.revenue.source)}${p.revenue.versionNumber ? ` (wersja ${p.revenue.versionNumber})` : ''}`}
            costs={p.costGrosze}
            result={p.resultGrosze}
            marginBp={p.marginBp}
          />
          <PlanVsActual p={p} />
          <CrewCosts p={p} />
          <ManualCosts eventId={eventId} p={p} editable={can(me, PROFIT_RESOURCE, 'edit')} onChanged={refresh} />
        </>
      )}
    </div>
  );
}
